import { Product } from '@airship-platform/airship-proposal';
import {
  buildMembershipPrices,
  mapProductPrices,
} from '@airship-platform/proposal-financing-feature/utils';
import { useCallback } from 'react';
import { AppointmentProposalOptionProduct } from '../../../data-access/appointmentProposalOptionProducts';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import {
  InvoiceTemplate,
  MembershipType,
} from '../membershipData/membershipData.collection';

type MapMembershipPricesRequest = {
  existingProducts: AppointmentProposalOptionProduct[];
  pricebookItems: Map<string, Product>;
  membershipTypes: MembershipType[];
  invoiceTemplates: InvoiceTemplate[];
  filters: {
    businessUnitId?: string;
    membershipTypeIds: string[];
  };
};

const dedupe = (items: string[]) => [...new Set(items)];

export const useProductMembershipPricing = () => {
  const data = useOfflineDbData();

  const getMembershipData = useCallback(async () => {
    const [membershipTypes, invoiceTemplates] = await Promise.all([
      data.find('membership_types'),
      data.find('invoice_templates'),
    ]);

    return { membershipTypes, invoiceTemplates };
  }, [data]);

  const getAppointments = useCallback(
    async (products: AppointmentProposalOptionProduct[]) => {
      const appointmentIds = dedupe(products.map((p) => p.appointmentId));
      if (!appointmentIds.length) {
        return [];
      }

      return data.find('appointments', {
        selector: { appointmentId: { $in: appointmentIds } },
      });
    },
    [data]
  );

  const getPricebookItems = useCallback(
    async (pricebookIds: string[]) => {
      const uniquePricebookIds = dedupe(pricebookIds);
      if (!uniquePricebookIds.length) {
        return new Map();
      }

      const res = await data.find('pricebook_items', {
        selector: { pricebookId: { $in: uniquePricebookIds } },
      });
      return new Map(res.map((d) => [d.pricebookId, d]));
    },
    [data]
  );

  const mapMembershipPrices = useCallback(
    ({
      existingProducts,
      pricebookItems,
      membershipTypes,
      invoiceTemplates,
      filters,
    }: MapMembershipPricesRequest) => {
      const mappedProducts = existingProducts.flatMap((product) =>
        mapProductPrices(
          { ...product, pricebookPrices: product.pricebookPrices || [] },
          pricebookItems
        )
      );

      const productFinancingDict = new Map(
        buildMembershipPrices({
          products: mappedProducts,
          membershipTypes,
          invoiceTemplates,
          filters: {
            businessUnitId: filters.businessUnitId,
            activeMembershipTypes: filters.membershipTypeIds,
          },
        }).products.map((p) => [p.appointmentProposalOptionProductId, p])
      );

      return existingProducts.map((p) => ({
        ...p,
        ...productFinancingDict.get(p.appointmentProposalOptionProductId),
      }));
    },
    []
  );

  const updateMembershipForProducts = useCallback(
    async (products: AppointmentProposalOptionProduct[]) => {
      const { membershipTypes, invoiceTemplates } = await getMembershipData();
      if (membershipTypes.length === 0 || invoiceTemplates.length === 0) {
        return products;
      }

      const appointments = await getAppointments(products);
      if (appointments.length === 0) {
        return products;
      }

      const pricebookItems = await getPricebookItems(
        products.map((p) => p.pricebookId)
      );

      const productsByOptionId = new Map<
        string,
        AppointmentProposalOptionProduct[]
      >();
      const appointmentIdsOptionsIdsMap = new Map<string, Set<string>>();

      for (const p of products) {
        const appointmentId = p.appointmentId;
        const optionId = p.appointmentProposalOptionId;

        if (p.status !== 'DELETED' && p.groupingName !== 'LINKED_ITEM') {
          const existing = productsByOptionId.get(optionId) ?? [];
          productsByOptionId.set(optionId, [...existing, p]);
          if (!appointmentIdsOptionsIdsMap.has(appointmentId)) {
            appointmentIdsOptionsIdsMap.set(appointmentId, new Set());
          }
          appointmentIdsOptionsIdsMap.get(appointmentId)?.add(optionId);
        }
      }

      const optionAndMemberships = appointments.flatMap((a) =>
        [...(appointmentIdsOptionsIdsMap.get(a.appointmentId) || [])].map(
          (optionId) => ({
            businessUnitId: a.job.businessUnitId,
            membershipTypeIds: a.memberships.map((m) => m.membershipTypeId),
            existingProducts: productsByOptionId.get(optionId) ?? [],
          })
        )
      );

      const items = optionAndMemberships.flatMap(
        ({ businessUnitId, membershipTypeIds, existingProducts }) =>
          mapMembershipPrices({
            existingProducts,
            pricebookItems,
            membershipTypes,
            invoiceTemplates,
            filters: { businessUnitId, membershipTypeIds },
          })
      );

      const processedIds = new Set(
        items.map((p) => p.appointmentProposalOptionProductId)
      );
      const unprocessed = products.filter(
        (p) => !processedIds.has(p.appointmentProposalOptionProductId)
      );

      return [...items, ...unprocessed];
    },
    [getMembershipData, getAppointments, getPricebookItems, mapMembershipPrices]
  );

  return {
    updateMembershipForProducts,
  };
};
